import React, { useEffect, useState } from "react";
import { UsersWrapper } from "./styled";
import {
  Box,
  ButtonStyled,
  LabelStyled,
  SubHeadingStyled,
  ThemeLoader,
} from "../../../components/styled/Elements";
import ListTable from "../../../components/common/tables/ListTable";
import { usersListColumns } from "../../../helpers/adminConstants/TableConstants";
import { useTranslation } from "react-i18next";
import {
  PlaceholderImg,
  PlusIcon,
  SearchIcon,
} from "../../../components/styled/AllImages";
import { Col, Row } from "react-bootstrap";
import FormRadio from "../../../components/common/radioButtons/FormRadio";
import FloatingInput from "../../../components/common/inputFields/FloatingInput";
import CustomTile from "../../../components/common/statusTiles/CustomTile";
import { usersTileArray } from "../../../helpers/adminConstants/AdminConstants";
import DeleteModal from "../../../components/admin/Modals/DeleteModal/DeleteModal";
import HistoryModal from "../../../components/admin/Modals/HistoryModal/HistoryModal";
import { useNavigate } from "react-router-dom";
import UsersApi from "../../../services/api/usersApi";
import { toast } from "react-toastify";
import { DropdownWithSearch } from "../../../components/common/dropdowns/DropdownInputs";
import {
  getFormattedDate,
  getFormattedTime,
  getModuleOperations,
} from "../../../utils";
import { useDispatch, useSelector } from "react-redux";
import { setUsersList } from "../../../store/slices/helperSlice";

const ASSET_URL = process.env.REACT_APP_ASSET_URL;

const Users = () => {
  const dispatch = useDispatch();
  const { hqBranchesList, branchesDeps } = useSelector((state) => state.helper);

  const {
    t,
    i18n: { language },
  } = useTranslation();
  const formattedHqBranchesList =
    hqBranchesList &&
    hqBranchesList.map((item) => ({
      id: item?.id,
      name: language === "en" ? item.name_en : item.name_ar,
    }));
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [userId, setUserId] = useState(null);
  const [users, setUsers] = useState([]);
  const [departmentsList, setDepartmentsList] = useState([]);
  const [activePage, setActivePage] = useState(1);
  const [perPage, setPerPage] = useState(null);
  const [total, setTotal] = useState(null);
  const [showDelete, setShowDelete] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [userHistory, setUserHistory] = useState({});
  const [filterParams, setFilterParams] = useState({
    search_status: null,
    search_department: null,
    search_value: null,
    search_branch: null,
  });

  useEffect(() => {
    if (branchesDeps && branchesDeps.length > 0) {
      const deptList = branchesDeps?.filter(
        (item) => item?.location_id === filterParams?.search_branch
      );
      setDepartmentsList(deptList);
    }
  }, [branchesDeps, filterParams]);

  const onChange = (e) => {
    let { name, value } = e.target;

    if (name === "search_department") value = parseInt(value);
    else if (name === "search_branch") value = parseInt(value);
    else if (name === "search_status")
      value = value === "all" ? "" : value === "2" ? "0" : value;

    setFilterParams((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const onDelete = (row) => {
    setUserId(row.id);
    setShowDelete(true);
  };

  const onHistory = (row) => {
    getUserHistory(row.id);
    setShowHistory(true);
  };

  const handleView = (user) => {
    navigate(`/users/view-user/${user.id}`);
  };

  const handleEdit = (user) => {
    navigate(`/users/edit-user/${user.id}`);
  };

  const handleAddUser = () => {
    if (
      hqBranchesList &&
      hqBranchesList.length > 0 &&
      branchesDeps &&
      branchesDeps.length > 0
    ) {
      navigate("/users/add-user");
    } else {
      toast.error(t("please_create_hq_department"));
    }
  };

  const handleStatusToggle = async (row) => {
    const newObj = {
      _method: "PUT",
      user_id: row.id,
      status: parseInt(row.status) === 1 ? 0 : 1,
    };
    try {
      const response = await UsersApi.changeUserStatus(newObj);
      if (response.code === 200) {
        toast.success(t("user_status_updated"));
        getUsersList();
      }
      if (response.code === 204 || response.code === 100) {
        toast.error(response.message);
      }
    } catch (error) {}
  };

  const handleDelete = async () => {
    try {
      const response = await UsersApi.deleteUser(userId);
      if (response.code === 200) {
        toast.success(response.message);
        setShowDelete(false);
        setUserId(null);
        getUsersList();
      }
      if (response.code === 204 || response.code === 100) {
        toast.error(response.message);
      }
      if (response.code === 400) {
        toast.error(response?.error);
      }
    } catch (error) {}
  };

  const getUserHistory = async (id) => {
    try {
      setHistoryLoading(true);
      const { data, code } = await UsersApi.getUserHistory(id);
      if (code === 200) {
        const newCreatedBy = {
          id: data?.id,
          name: data?.created_by_name,
          subLabel: data?.department && data?.branchHQ ? `${data?.department} (${data?.branchHQ})` : "",
          avatar: data?.created_by_profile
            ? `${ASSET_URL}/${data?.created_by_profile}`
            : PlaceholderImg,
          date: getFormattedDate(data?.created_at),
          time: getFormattedTime(data?.created_at),
        };
        const editHistory =
          data?.update_history?.length > 0 &&
          data?.update_history?.map((user) => ({
            id: user.id,
            name: user.update_by_name,
            subLabel: user?.update_by_detail ? `${
              language === "en"
                ? user.update_by_detail.department_selective.name_en
                : user.update_by_detail.department_selective.name_ar
            } (${
              language === "en"
                ? user.update_by_detail.office_location_selective.name_en
                : user.update_by_detail.office_location_selective.name_ar
            })` : "",
            avatar: user.update_by_profile
              ? `${ASSET_URL}/${user.update_by_profile}`
              : PlaceholderImg,
            date: getFormattedDate(user.updated_at),
            time: getFormattedTime(user.updated_at),
          }));

        setUserHistory((prevData) => ({
          ...prevData,
          createdBy: newCreatedBy,
          editHistory: editHistory,
        }));
      }
    } catch (error) {
    } finally {
      setHistoryLoading(false);
    }
  };

  const getUsersList = async () => {
    try {
      setLoading(true);
      const { data, code } = await UsersApi.usersList(activePage, filterParams);

      if (code === 200) {
        usersTileArray[0].count = data?.all?.total;
        usersTileArray[1].count = data?.user;
        usersTileArray[2].count = data?.admin;
        usersTileArray[3].count = data?.active;
        usersTileArray[4].count = data?.inactive;
        const usersArr = data?.all?.data.map((user) => ({
          ...user,
          profile: user?.profile
            ? `${ASSET_URL}/${user?.profile}`
            : PlaceholderImg,
          ...(user?.user_detail?.department && {
            department: {
              dep:
                language === "en"
                  ? user?.user_detail?.department.name_en
                  : user?.user_detail?.department.name_ar,
              subLabel:
                language === "en"
                  ? user?.user_detail?.office_location?.name_en
                  : user?.user_detail?.office_location?.name_ar,
            },
          }),
        }));

        setUsers(usersArr);
        setPerPage(data?.all?.per_page);
        setTotal(data?.all?.total);
      } else {
        usersTileArray[0].count = 0;
        usersTileArray[1].count = 0;
        usersTileArray[2].count = 0;
        usersTileArray[3].count = 0;
        setUsers([]);
        setPerPage(null);
        setTotal(null);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getUsersList();
  }, [activePage, language, filterParams]);

  const getUsersListWithoutPg = async () => {
    try {
      const { data, code } = await UsersApi.getUsersListWithoutPg();

      if (code === 200) {
        if (data && data.length > 0) {
          const updatedUsers = data?.map((user) => ({
            ...user,
            role: user?.position,
            subLabel: `${
              language === "en"
                ? user?.user_detail?.department_selective?.name_en
                : user?.user_detail?.department_selective?.name_ar
            }  (${
              language === "en"
                ? user?.user_detail?.office_location_selective?.name_en
                : user?.user_detail?.office_location_selective?.name_ar
            })`,
          }));
          dispatch(setUsersList(updatedUsers));
        }
      }
    } catch (error) {}
  };

  useEffect(() => {
    getUsersListWithoutPg();
  }, []);

  return (
    <UsersWrapper>
      <Box className="content-head">
        <SubHeadingStyled size="18px" weight="700" color="var(--text)">
          {t("users")}
        </SubHeadingStyled>

        {getModuleOperations(4, 2) && (
          <Box>
            <ButtonStyled
              label={t("add_user")}
              icon={PlusIcon}
              onClick={handleAddUser}
            />
          </Box>
        )}
      </Box>

      <Row className="mx-2 mt-2">
        <Col className="my-2">
          <Box className="d-flex type-select-box">
            <LabelStyled size="14px" className="mx-2">
              {t("type")}
            </LabelStyled>
            <Box className="d-flex filters-list">
              <FormRadio
                name="search_status"
                subLabel={t("all")}
                value={"all"}
                checked={!filterParams.search_status}
                onChange={onChange}
              />
              <FormRadio
                name="search_status"
                subLabel={t("activated")}
                value={"1"}
                checked={filterParams.search_status === "1"}
                onChange={onChange}
              />
              <FormRadio
                name="search_status"
                subLabel={t("deactivated")}
                value={"2"}
                checked={filterParams.search_status === "0"}
                onChange={onChange}
                className={"text-nowrap"}
              />
            </Box>
          </Box>
        </Col>
        <Col className="my-2">
          <DropdownWithSearch
            name={"search_branch"}
            optionsList={formattedHqBranchesList}
            selectedoption={filterParams?.search_branch}
            placeholder={t("search_by_hqbranch")}
            onChange={onChange}
          />
        </Col>
        <Col className="my-2">
          <DropdownWithSearch
            name={"search_department"}
            optionsList={departmentsList}
            selectedoption={filterParams?.search_department}
            placeholder={t("search_by_department")}
            onChange={onChange}
          />
        </Col>
        <Col className="my-2">
          <FloatingInput
            name="search_value"
            type="input"
            fieldType="text"
            label={t("search")}
            placeholder={t("search")}
            bg="var(--white)"
            icon={SearchIcon}
            onChange={onChange}
          />
        </Col>
      </Row>

      <Row className="mx-2">
        {usersTileArray.map((tile, key) => (
          <Col lg={3} md={6} sm={12} className="my-2">
            <CustomTile key={key} {...tile} />
          </Col>
        ))}
      </Row>
      <Box>
        {loading ? (
          <ThemeLoader />
        ) : (
          <ListTable
            options={["view", "info", "edit", "status", "delete"]}
            typeId={4}
            rows={users}
            columns={usersListColumns}
            onDelete={onDelete}
            onInfoView={onHistory}
            onViewDetails={handleView}
            onEdit={handleEdit}
            onStatusToggle={handleStatusToggle}
            activePage={activePage}
            setActivePage={setActivePage}
            totalCount={total}
            perPage={perPage}
          />
        )}
      </Box>
      <HistoryModal
        title={t("user_history")}
        show={showHistory}
        onHide={() => setShowHistory(false)}
        createdBy={userHistory.createdBy}
        editHistory={userHistory.editHistory}
        historyLoading={historyLoading}
      />
      <DeleteModal
        show={showDelete}
        onHide={() => setShowDelete(false)}
        title={t("user")}
        onDelete={handleDelete}
      />
    </UsersWrapper>
  );
};

export default Users;
